import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type {
  RiskRegisterResponse,
  RiskRegisterResponseRow,
} from '@risksmart-app/trpc/src/types';
import type { GetRisksFlatQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

function mapTrpcRiskToGraphQL(
  risk: RiskRegisterResponseRow
): GetRisksFlatQuery['risk'][number] {
  return {
    ...risk,
    // impactRatingsForTrend is the same data as impactRatings but used for trend calculation
    // The ordering is already handled in the query
    impactRatingsForTrend: risk.impactRatings,
    controls_aggregate: {
      aggregate: {
        count: risk.controls.length,
      },
    },
    indicators_aggregate: {
      aggregate: {
        count: risk.indicators.length,
      },
    },
    actions_aggregate: {
      aggregate: {
        count: risk.actions.length,
      },
    },
  };
}

/**
 * Maps TRPC risk data to match the GraphQL query structure
 */
export function mapTrpcRisksToGraphQL(
  trpcData: RiskRegisterResponse | undefined
): GetRisksFlatQuery | undefined {
  if (!trpcData) {
    return undefined;
  }

  return {
    risk: trpcData.risk.map((risk) => mapTrpcRiskToGraphQL(risk)),
  };
}

export const useGetRiskRegister = (skip?: boolean) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();
  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.risk.register.queryOptions(),

    enabled: trpcEnabled && !skip, // Only enable TRPC query when flag is true
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = mapTrpcRisksToGraphQL(trpcData);

  return {
    loading: trpcLoading,
    data,
    refetch: () => trpcRefetch(),
  };
};
